import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'freya-tour-state';
const CURRENT_VERSION = 1;

// Legacy keys to migrate from
const LEGACY_KEYS = {
  seenStepIds: 'hasSeenStepIds',
  seenWelcome: 'hasSeenTourWelcome',
  skippedAll: 'hasSkippedAllTours',
  activeSequence: 'activeTourSequence',
} as const;

export interface ActiveTourPosition {
  sequenceId: string | null;
  tourId: string;
  tourIndex: number;
  stepIndex: number;
}

export interface TourPersistenceState {
  version: number;
  hasSeenWelcome: boolean;
  hasSkippedAll: boolean;
  completedTours: string[];
  active: ActiveTourPosition | null;
}

const DEFAULT_STATE: TourPersistenceState = {
  version: CURRENT_VERSION,
  hasSeenWelcome: false,
  hasSkippedAll: false,
  completedTours: [],
  active: null,
};

/**
 * Safely read and parse JSON from localStorage.
 */
function readFromStorage(): TourPersistenceState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return migrateFromLegacy();

    const parsed = JSON.parse(raw);

    // Version mismatch → reset (future: add migration logic per version)
    if (parsed.version !== CURRENT_VERSION) {
      return { ...DEFAULT_STATE };
    }

    // Validate shape
    return {
      version: CURRENT_VERSION,
      hasSeenWelcome: Boolean(parsed.hasSeenWelcome),
      hasSkippedAll: Boolean(parsed.hasSkippedAll),
      completedTours: Array.isArray(parsed.completedTours) ? parsed.completedTours : [],
      active: parsed.active ?? null,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

/**
 * Migrate from legacy localStorage keys (3 separate keys + sessionStorage)
 * to the new unified schema. Cleans up old keys after migration.
 */
function migrateFromLegacy(): TourPersistenceState {
  const state = { ...DEFAULT_STATE };

  try {
    // Migrate hasSeenTourWelcome
    const welcomeRaw = localStorage.getItem(LEGACY_KEYS.seenWelcome);
    if (welcomeRaw) {
      state.hasSeenWelcome = JSON.parse(welcomeRaw) === true;
    }

    // Migrate hasSkippedAllTours
    const skippedRaw = localStorage.getItem(LEGACY_KEYS.skippedAll);
    if (skippedRaw) {
      state.hasSkippedAll = JSON.parse(skippedRaw) === true;
    }

    // Migrate hasSeenStepIds → extract unique tour IDs from step IDs
    // Old format: ["top-widget-logo", "top-widget-upload-button", "sidebar-audit", ...]
    // We extract the tour prefix (e.g., "top-widget", "sidebar") as completed tours
    const seenRaw = localStorage.getItem(LEGACY_KEYS.seenStepIds);
    if (seenRaw) {
      const seenIds: string[] = JSON.parse(seenRaw);
      const tourIds = new Set<string>();

      // Known tour IDs to match against
      const knownTourIds = ['top-widget', 'sidebar', 'audit-landing', 'audit-dashboard'];

      for (const stepId of seenIds) {
        // Check if the stepId IS a tour ID (tours were also added to seenIds on skip)
        if (knownTourIds.includes(stepId)) {
          tourIds.add(stepId);
        } else {
          // Extract tour prefix from step ID (e.g., "top-widget-logo" → "top-widget")
          const matchedTour = knownTourIds.find((tourId) => stepId.startsWith(`${tourId}-`));
          if (matchedTour) {
            tourIds.add(matchedTour);
          }
        }
      }

      state.completedTours = [...tourIds];
    }

    // Migrate activeTourSequence from sessionStorage
    const sequenceRaw = sessionStorage.getItem(LEGACY_KEYS.activeSequence);
    if (sequenceRaw) {
      const { sequence, index } = JSON.parse(sequenceRaw);
      if (sequence && typeof index === 'number') {
        const tourId = sequence.tours?.[index];
        state.active = {
          sequenceId: sequence.id ?? null,
          tourId: tourId ?? '',
          tourIndex: index,
          stepIndex: 0,
        };
      }
    }
  } catch {
    // If migration fails, start fresh
    return { ...DEFAULT_STATE };
  }

  // Clean up legacy keys
  cleanupLegacyKeys();

  return state;
}

/**
 * Remove all legacy storage keys.
 */
function cleanupLegacyKeys(): void {
  try {
    localStorage.removeItem(LEGACY_KEYS.seenStepIds);
    localStorage.removeItem(LEGACY_KEYS.seenWelcome);
    localStorage.removeItem(LEGACY_KEYS.skippedAll);
    sessionStorage.removeItem(LEGACY_KEYS.activeSequence);
  } catch {
    // Ignore cleanup errors
  }
}

/**
 * Safely write state to localStorage.
 */
function writeToStorage(state: TourPersistenceState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore write errors (e.g., quota exceeded)
  }
}

/**
 * Hook that manages all tour persistence in a single localStorage key.
 *
 * Provides the current state and mutation functions.
 * All mutations auto-persist to localStorage.
 */
export function useTourPersistence() {
  const [state, setState] = useState<TourPersistenceState>(readFromStorage);

  // Persist on every state change
  useEffect(() => {
    writeToStorage(state);
  }, [state]);

  const markTourCompleted = useCallback((tourId: string) => {
    setState((prev) => {
      const completedSet = new Set(prev.completedTours);
      completedSet.add(tourId);
      return {
        ...prev,
        completedTours: [...completedSet],
      };
    });
  }, []);

  const isTourCompleted = useCallback((tourId: string) => state.completedTours.includes(tourId), [state.completedTours]);

  const setActive = useCallback((position: ActiveTourPosition) => {
    setState((prev) => ({
      ...prev,
      active: position,
    }));
  }, []);

  const clearActive = useCallback(() => {
    setState((prev) => ({
      ...prev,
      active: null,
    }));
  }, []);

  const updateStepIndex = useCallback((stepIndex: number) => {
    setState((prev) => {
      if (!prev.active) return prev;
      return {
        ...prev,
        active: { ...prev.active, stepIndex },
      };
    });
  }, []);

  const setWelcomeSeen = useCallback(() => {
    setState((prev) => ({
      ...prev,
      hasSeenWelcome: true,
    }));
  }, []);

  const setSkippedAll = useCallback(
    (skipped: boolean) =>
      setState((prev) => ({
        ...prev,
        hasSkippedAll: skipped,
      })),
    []
  );

  const clearCompletedTours = useCallback(() => {
    setState((prev) => ({
      ...prev,
      completedTours: [],
    }));
  }, []);

  const resetTour = useCallback((tourId: string) => {
    setState((prev) => ({
      ...prev,
      completedTours: prev.completedTours.filter((id) => id !== tourId),
      active: prev.active?.tourId === tourId ? null : prev.active,
    }));
  }, []);

  const resetAll = useCallback(() => {
    setState({ ...DEFAULT_STATE });
  }, []);

  return {
    state,
    markTourCompleted,
    isTourCompleted,
    setActive,
    clearActive,
    clearCompletedTours,
    updateStepIndex,
    setWelcomeSeen,
    setSkippedAll,
    resetTour,
    resetAll,
  };
}
